import { z } from "zod/v4";

import { BaseRequestArgs } from "../base-reader";

export interface UserFeesRequestArgs extends BaseRequestArgs {
  /** The subaccount address to get fees and fee schedule for */
  subAddr: string;
}

/** Per-day trading volume entry for the current on-chain fee window. */
export const DailyUserVolumeSchema = z.object({
  /** Date in YYYY-MM-DD format (UTC) */
  date: z.string(),
  /** Total volume (USD, whole-dollar integer string) */
  volume: z.string(),
  /** Maker-side volume (USD, whole-dollar integer string) */
  maker_volume: z.string(),
  /** Taker-side volume (USD, whole-dollar integer string) */
  taker_volume: z.string(),
});

/**
 * A single VIP (volume-based) fee tier. Users qualify once their on-chain
 * fee-window volume reaches `volume_threshold` USD (inclusive, matches on-chain `>=`).
 */
export const VipTierSchema = z.object({
  /** Minimum fee-window USD volume (inclusive) to reach this tier, whole-dollar integer string */
  volume_threshold: z.string(),
  /** Taker fee rate at this tier (decimal, e.g. 0.000300 = 0.03%) */
  taker: z.number(),
  /** Maker fee rate at this tier (decimal, e.g. 0.000090 = 0.009%) */
  maker: z.number(),
});

/** A single market-maker rebate tier (empty when rebates are disabled). */
export const MarketMakerTierSchema = z.object({
  /** Fraction of global volume the user must provide as maker (decimal string, e.g. "0.005") */
  maker_fraction_threshold: z.string(),
  /** Maker rebate rate (negative decimal, e.g. -0.000010) */
  maker: z.number(),
});

/** Grouped fee tier schedules. */
export const FeeTiersSchema = z.object({
  /** Volume-based VIP tiers above the base (tier 0 rates live on the parent schedule) */
  vip: z.array(VipTierSchema),
  /** Market-maker rebate tiers (empty when rebates are disabled) */
  market_maker: z.array(MarketMakerTierSchema),
});

/** Fee schedule mirroring the current on-chain default tiers. Account-independent. */
export const FeeScheduleSchema = z.object({
  /** Base taker fee (tier 0, no volume requirement), decimal e.g. 0.000340 */
  taker: z.number(),
  /** Base maker fee (tier 0, no volume requirement), decimal e.g. 0.000110 */
  maker: z.number(),
  /** All fee tiers above the base */
  tiers: FeeTiersSchema,
  /** Referral discount fraction applied to referred users (0.0 when disabled) */
  referral_discount: z.number(),
});

/**
 * Per-product (perp or spot) fee state: the product's own rate ladder, the
 * user's effective rates at the shared cross-product tier, and the product's
 * raw (unweighted) volume history.
 */
export const ProductFeeStateSchema = z.object({
  /**
   * This product's fee tier index (0 = base). Perp's tier comes from
   * perp-only window volume; spot's from the WEIGHTED cross-product volume.
   * The two can differ for the same user.
   */
  fee_tier: z.number(),
  /** Rate ladder for THIS product (spot bps differ from perp at every tier) */
  fee_schedule: FeeScheduleSchema,
  /** Effective taker rate for this product after any product-specific discount */
  user_taker_rate: z.number(),
  /** Effective maker rate for this product after any product-specific discount */
  user_maker_rate: z.number(),
  /** This product's own daily volume history for the fee window (raw USD, NOT weighted) */
  daily_user_volume: z.array(DailyUserVolumeSchema),
  /** Sum of `daily_user_volume` over the window (USD, whole-dollar integer string) */
  total_window_volume_usd: z.string(),
  /** Product-specific active referral discount (0.0 for spot, which has no referral program) */
  active_referral_discount: z.number(),
});

/**
 * Cross-product volume multipliers used to compute the unified fee tier.
 * Mirrors on-chain `CrossProductVolumeWeights` (100 == 1.0x).
 */
export const VolumeWeightsSchema = z.object({
  /** Perp volume multiplier (e.g. 1.0) */
  perp: z.number(),
  /** Spot volume multiplier (e.g. 2.0) */
  spot: z.number(),
});

/**
 * Response for `GET /api/v1/user_fee_rates?account=<address>`.
 *
 * The fee tier is CROSS-PRODUCT: computed from
 * `perp_volume x volume_weights.perp + spot_volume x volume_weights.spot`
 * and indexes into each product's own rate ladder. The top-level fields
 * remain PERP-ONLY aliases of `perp.*` for backward compatibility; new
 * consumers should read `perp` / `spot` explicitly.
 */
export const UserFeesSchema = z.object({
  /** The queried account address */
  account: z.string(),
  /** Daily PERP volume breakdown for the fee window (back-compat alias of `perp.daily_user_volume`) */
  daily_user_volume: z.array(DailyUserVolumeSchema),
  /** PERP fee schedule (back-compat alias of `perp.fee_schedule`) */
  fee_schedule: FeeScheduleSchema,
  /** Effective PERP taker rate (back-compat alias of `perp.user_taker_rate`) */
  user_taker_rate: z.number(),
  /** Effective PERP maker rate (back-compat alias of `perp.user_maker_rate`) */
  user_maker_rate: z.number(),
  /** PERP fee tier index (0 = base tier); back-compat alias of `perp.fee_tier`. Spot's tier is `spot.fee_tier`. */
  fee_tier: z.number(),
  /** Active PERP referral discount fraction (back-compat alias of `perp.active_referral_discount`) */
  active_referral_discount: z.number(),
  /** Perp-side fee state (rates, ladder, raw volume history) */
  perp: ProductFeeStateSchema.optional(), // TODO: Remove optional once back-end is deployed
  /** Spot-side fee state (rates, ladder, raw volume history) */
  spot: ProductFeeStateSchema.optional(), // TODO: Remove optional once back-end is deployed
  /** Weighted cross-product volume driving `spot.fee_tier` (USD, whole-dollar integer string) */
  weighted_volume_usd: z.string().optional(), // TODO: Remove optional once back-end is deployed
  /** The multipliers used to compute `weighted_volume_usd` */
  volume_weights: VolumeWeightsSchema.optional(), // TODO: Remove optional once back-end is deployed
});

export type DailyUserVolume = z.infer<typeof DailyUserVolumeSchema>;
export type VipTier = z.infer<typeof VipTierSchema>;
export type MarketMakerTier = z.infer<typeof MarketMakerTierSchema>;
export type FeeTiers = z.infer<typeof FeeTiersSchema>;
export type FeeSchedule = z.infer<typeof FeeScheduleSchema>;
export type ProductFeeState = z.infer<typeof ProductFeeStateSchema>;
export type VolumeWeights = z.infer<typeof VolumeWeightsSchema>;
export type UserFees = z.infer<typeof UserFeesSchema>;
